import { FC } from 'react';
import { Box, Image, VStack } from '@chakra-ui/react';

import Clipboard from 'assets/brand/clipboard@3x.png';
import useCurrentBankAccountId from 'common/hooks/useCurrentBankAccountId';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import BankAccountPicker from 'components/BankAccountPicker';
import { ALL_ACCOUNTS_VALUE } from 'components/BankAccountPicker/constants';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import * as Layout from 'layouts/ContentLayout';

import locales from './locales';
import SectionList from './SectionList';
import useFetchBankAndFeeStatements from './useFetchBankAndFeeStatements';
import { mapStatementsToSectionList } from './utils';

type EmptyBankStatementsProps = {
  text: string;
};

const EmptyBankStatements: FC<EmptyBankStatementsProps> = ({ text }) => {
  return (
    <VStack gap={16}>
      <Image boxSize={90} src={Clipboard} />
      <Text textAlign="center" variant="light">
        {text}
      </Text>
    </VStack>
  );
};

const BankStatements: FC = () => {
  const { currentBankAccountId, setCurrentBankAccountId } =
    useCurrentBankAccountId();
  const {
    bankAccounts,
    bankStatements,
    error,
    feeStatements,
    fetchMore,
    hasNextPage,
    loading,
    totalStatementsCount,
  } = useFetchBankAndFeeStatements(currentBankAccountId);

  if (error) {
    return <ErrorCard />;
  }

  if (loading) {
    return <Loader />;
  }

  const hasMultipleBankAccounts = bankAccounts.length > 1;
  const sections = mapStatementsToSectionList(
    bankStatements,
    feeStatements,
    bankAccounts,
  );

  const handleBankAccountChange = (bankAccountId: string | undefined) => {
    setCurrentBankAccountId(bankAccountId);
  };

  if (!hasMultipleBankAccounts && totalStatementsCount === 0) {
    return (
      <Layout.Root>
        <Layout.Body>
          <VStack alignItems="flex-start" spacing="space-32">
            <BackButton />
            <Heading size="lg">{locales.title}</Heading>
          </VStack>
          <Box paddingTop="space-32">
            <EmptyBankStatements text={locales.noStatements} />
          </Box>
        </Layout.Body>
      </Layout.Root>
    );
  }

  return (
    <Layout.Root>
      <Layout.Body>
        <VStack alignItems="flex-start" spacing="space-32">
          <BackButton />
          <Heading size="lg">{locales.title}</Heading>
          {hasMultipleBankAccounts ? (
            <BankAccountPicker
              key={currentBankAccountId}
              onBankAccountChange={handleBankAccountChange}
              selectedBankAccountId={currentBankAccountId ?? ALL_ACCOUNTS_VALUE}
              withAllAccountsOption
            />
          ) : null}
        </VStack>
        <Box paddingTop="space-16">
          {sections.length > 0 ? (
            <SectionList
              fetchMore={fetchMore}
              hasMore={hasNextPage}
              sections={sections}
            />
          ) : (
            <EmptyBankStatements
              text={
                !currentBankAccountId
                  ? locales.noStatements
                  : locales.noStatementsForAccount
              }
            />
          )}
        </Box>
      </Layout.Body>
    </Layout.Root>
  );
};

export default BankStatements;
